import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'react-qr-code';
import api from '../services/api';
import './Results.css';

const PUBLIC_RESULTS_URL = 'https://fantaseasharkfishing.com';
const WORKER_URL = process.env.REACT_APP_WORKER_URL || 'https://fantasea-fishing-results-worker.fantaseasharkfishing.workers.dev';

const Results = ({ isFullscreen, toggleFullscreen }) => {
    const [mainLeaderboard, setMainLeaderboard] = useState([]);
    const [calcuttaLeaderboard, setCalcuttaLeaderboard] = useState([]);
    const [calcuttaWinnersCount, setCalcuttaWinnersCount] = useState(3);
    const [showCalcutta, setShowCalcutta] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [currentRound, setCurrentRound] = useState(null);
    const [sponsorLogos, setSponsorLogos] = useState([]);
    const [sponsorIndex, setSponsorIndex] = useState(0);
    const [sponsorFading, setSponsorFading] = useState(false);
    const [activeTab, setActiveTab] = useState('regular');
    const [showDetails, setShowDetails] = useState(false);
    const [installPrompt, setInstallPrompt] = useState(null);
    const mainScrollableTableRef = useRef(null);
    const calcuttaScrollableTableRef = useRef(null);
    const isPublicMode = process.env.REACT_APP_PUBLIC_MODE === 'true';

    // Android "Add to Home Screen" install prompt
    useEffect(() => {
        if (!isPublicMode) return;
        const handler = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [isPublicMode]);

    const handleInstall = () => {
        if (!installPrompt) return;
        installPrompt.prompt();
        installPrompt.userChoice.then(() => setInstallPrompt(null));
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                if (isPublicMode) {
                    setCalcuttaWinnersCount(3);
                    setShowCalcutta(true);
                    return;
                }
                const response = await api.get('/results');
                setCalcuttaWinnersCount(response.data.numCalcuttaWinners);
                setShowCalcutta(response.data.showCalcutta);
            } catch (error) {
                console.error('Error fetching settings:', error);
            }
        };

        const fetchSponsors = async () => {
            if (isPublicMode) return;
            try {
                const response = await api.get('/settings/sponsors');
                setSponsorLogos(response.data.logos || []);
            } catch (error) {
                console.error('Error fetching sponsor logos:', error);
            }
        };

        fetchSettings();
        fetchSponsors();

        let eventSource;
        let pollInterval;

        if (isPublicMode) {
            const fetchPublicData = async () => {
                try {
                    const response = await fetch(WORKER_URL);
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                    const data = await response.json();
                    setMainLeaderboard(data.main || []);
                    setCalcuttaLeaderboard(data.calcutta || []);
                    if (data.current_round) setCurrentRound(data.current_round);
                    if (data.show_calcutta !== undefined) setShowCalcutta(data.show_calcutta);
                    setLastRefreshed(new Date());
                } catch (error) {
                    console.error("Failed to fetch public results:", error);
                }
            };
            fetchPublicData();
            pollInterval = setInterval(fetchPublicData, 10000);
        } else {
            const streamUrl = `/api/leaderboard/stream`;
            eventSource = new EventSource(streamUrl);
            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.error) { console.error("SSE Error:", data.error); return; }
                setMainLeaderboard(data.main);
                setCalcuttaLeaderboard(data.calcutta);
                setCurrentRound(data.current_round);
                if (data.show_calcutta !== undefined) setShowCalcutta(data.show_calcutta);
                setLastRefreshed(new Date());
            };
            eventSource.onerror = (err) => console.error("EventSource failed:", err);

            // Auto-scroll only on the local/admin display (not on mobile public view)
            const scrollInterval = setInterval(() => {
                [mainScrollableTableRef, calcuttaScrollableTableRef].forEach((ref) => {
                    if (!ref.current) return;
                    const { scrollTop, scrollHeight, clientHeight } = ref.current;
                    if (scrollHeight <= clientHeight) return;
                    if (scrollTop + clientHeight >= scrollHeight) {
                        ref.current.scrollTop = 0;
                    } else {
                        ref.current.scrollTop += 1;
                    }
                });
            }, 50);

            return () => {
                if (eventSource) eventSource.close();
                clearInterval(scrollInterval);
            };
        }

        return () => {
            if (eventSource) eventSource.close();
            if (pollInterval) clearInterval(pollInterval);
        };
    }, [isPublicMode]);

    // Sponsor rotation with fade transition
    useEffect(() => {
        if (sponsorLogos.length <= 1) return;
        const intervalId = setInterval(() => {
            setSponsorFading(true);
            setTimeout(() => {
                setSponsorIndex((prev) => (prev + 1) % sponsorLogos.length);
                setSponsorFading(false);
            }, 600);
        }, 6000);
        return () => clearInterval(intervalId);
    }, [sponsorLogos]);

    const formatWeight = (lbs, oz) => (lbs || oz) ? `${lbs} lb ${oz} oz` : '-';

    const mainWinners = mainLeaderboard.slice(0, 3);
    const mainOthers = mainLeaderboard.slice(3);

    const placeLabel = (i) => ['1st', '2nd', '3rd'][i] || `${i + 1}th`;

    return (
        <main className={`results-main ${isFullscreen ? 'is-fullscreen' : ''}`}>
            {/* Fullscreen toggle — hidden on public mobile, visible on admin/tablet */}
            {(!isPublicMode || isFullscreen) && (
                <button onClick={toggleFullscreen} className="fullscreen-toggle-button">
                    {isFullscreen ? 'Exit Fullscreen' : 'Go Fullscreen'}
                </button>
            )}

            {/* QR code panel (local/admin view only) */}
            {!isPublicMode && (
                <div className={`qr-panel${isFullscreen ? ' qr-panel-fullscreen' : ''}`}>
                    <QRCode value={PUBLIC_RESULTS_URL} size={isFullscreen ? 72 : 88} />
                    <p className="qr-label">Scan for live results</p>
                </div>
            )}

            {/* Header */}
            <div className="results-header">
                <img src="/images/rotary_cog.png" alt="Rotary" className="results-header-logo" />
                <div className="results-title-block">
                    <h2 className="results-title">Tournament Leaderboard</h2>
                    {currentRound && <span className="results-round-badge">Round {currentRound}</span>}
                </div>
                <img src="/images/FantaSea_Logo.png" alt="FantaSea" className="results-header-logo" />
            </div>

            {/* Sponsor banner */}
            {sponsorLogos.length > 0 && (
                <div className="sponsor-banner">
                    <img
                        key={sponsorIndex}
                        src={sponsorLogos[sponsorIndex]}
                        alt="Sponsor"
                        className={`sponsor-banner-img ${sponsorFading ? 'fading' : 'visible'}`}
                    />
                    {sponsorLogos.length > 1 && (
                        <div className="sponsor-dots">
                            {sponsorLogos.map((_, i) => (
                                <span key={i} className={`sponsor-dot ${i === sponsorIndex ? 'active' : ''}`} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            <p className="last-refreshed">
                {isPublicMode && <span className="live-dot" title="Live" />}
                Last updated: {lastRefreshed ? lastRefreshed.toLocaleTimeString() : '—'}
            </p>

            {/* Mobile tab nav — only shown in public mode on small screens via CSS */}
            {isPublicMode && showCalcutta && (
                <div className="mobile-tabs">
                    <button
                        className={`mobile-tab ${activeTab === 'regular' ? 'active' : ''}`}
                        onClick={() => setActiveTab('regular')}
                    >
                        Regular Event
                    </button>
                    <button
                        className={`mobile-tab ${activeTab === 'calcutta' ? 'active' : ''}`}
                        onClick={() => setActiveTab('calcutta')}
                    >
                        Calcutta
                    </button>
                </div>
            )}

            {/* Details toggle — public mobile only */}
            {isPublicMode && (
                <div className="details-toggle-wrap">
                    <button
                        className={`details-toggle-btn ${showDetails ? 'active' : ''}`}
                        onClick={() => setShowDetails(d => !d)}
                    >
                        {showDetails ? '▴ Hide details' : '▾ Show details'}
                    </button>
                </div>
            )}

            {/* Leaderboard columns */}
            <div className="results-container">
                {/* Regular event */}
                <div className={`event-column ${isPublicMode && activeTab !== 'regular' ? 'tab-hidden' : ''} ${showDetails ? 'show-details' : ''}`}>
                    <h3 className="column-title">Regular Event</h3>
                    <div className="event-content">
                        <div className="winners-section">
                            {mainLeaderboard.length === 0 ? (
                                <div className="no-results">No results yet.</div>
                            ) : (
                                mainWinners.map((winner, i) => (
                                    <div key={winner.license_number} className={`winner winner-${i + 1}`}>
                                        <span className="winner-place">{placeLabel(i)}</span>
                                        <span className="winner-name">
                                            {winner.name || winner.business || winner.license_number}
                                        </span>
                                        <span className="winner-weight">
                                            {formatWeight(winner.total_Pounds, winner.total_OuncesRemainder)}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="scrollable-table" ref={mainScrollableTableRef}>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="col-rank">#</th>
                                        <th className="col-id col-mobile-hidden">ID</th>
                                        <th className="col-name">Angler</th>
                                        <th className="col-shark">Shark 1</th>
                                        <th className="col-shark">Shark 2</th>
                                        <th className="col-shark">Shark 3</th>
                                        <th className="col-total">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mainLeaderboard.length === 0 ? (
                                        <tr><td colSpan="7">No results to display.</td></tr>
                                    ) : (
                                        mainOthers.map((row, index) => (
                                            <tr key={row.license_number || `main-${index}`}>
                                                <td className="col-rank">{row.rank || ''}</td>
                                                <td className="col-id col-mobile-hidden">{row.license_number || ''}</td>
                                                <td className="col-name">{row.name || row.business || row.license_number || ''}</td>
                                                {[1, 2, 3].map((round) => {
                                                    const shark = (row.sharks || []).find(s => s && s.round === round);
                                                    return (
                                                        <td key={round} className={`col-shark ${shark?.excluded ? 'strikethrough' : ''}`}>
                                                            <span className="shark-label">S{round}</span>
                                                            {shark ? formatWeight(shark.pounds, shark.ounces) : '—'}
                                                        </td>
                                                    );
                                                })}
                                                <td className="col-total">{formatWeight(row.total_Pounds, row.total_OuncesRemainder)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Calcutta event */}
                {showCalcutta && calcuttaWinnersCount > 0 && (
                    <div className={`event-column ${isPublicMode && activeTab !== 'calcutta' ? 'tab-hidden' : ''} ${showDetails ? 'show-details' : ''}`}>
                        <h3 className="column-title">Loser Calcutta</h3>
                        <div className="event-content">
                            <div className="winners-section">
                                {calcuttaLeaderboard.length === 0 ? (
                                    <div className="no-results">No results yet.</div>
                                ) : (() => {
                                    const winner = calcuttaLeaderboard[0];
                                    return winner ? (
                                        <div key={winner.license_number} className="winner winner-1">
                                            <span className="winner-place">Winner</span>
                                            <span className="winner-name">
                                                {winner.name || winner.business || winner.license_number}
                                            </span>
                                            <span className="winner-weight">
                                                {formatWeight(winner.total_Pounds, winner.total_OuncesRemainder)}
                                            </span>
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                            <div className="scrollable-table" ref={calcuttaScrollableTableRef}>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th className="col-rank">#</th>
                                            <th className="col-id col-mobile-hidden">ID</th>
                                            <th className="col-name">Angler</th>
                                            {[1, 2, 3].map((round) => (
                                                <th key={round} className="col-shark">Round {round}</th>
                                            ))}
                                            <th className="col-total">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {calcuttaLeaderboard.length === 0 ? (
                                            <tr><td colSpan="7">No results to display.</td></tr>
                                        ) : (
                                            calcuttaLeaderboard.map((row, index) => (
                                                <tr key={row.license_number || `calcutta-${index}`}>
                                                    <td className="col-rank">{row.rank || ''}</td>
                                                    <td className="col-id col-mobile-hidden">{row.license_number || ''}</td>
                                                    <td className="col-name">{row.name || row.business || row.license_number || ''}</td>
                                                    {[1, 2, 3].map((round) => {
                                                        const shark = (row.sharks || []).find(s => s && s.round === round);
                                                        return (
                                                            <td key={round} className={`col-shark ${shark?.excluded ? 'strikethrough' : ''}`}>
                                                                <span className="shark-label">R{round}</span>
                                                                {shark ? formatWeight(shark.pounds, shark.ounces) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="col-total">{formatWeight(row.total_Pounds, row.total_OuncesRemainder)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Android install banner */}
            {installPrompt && (
                <div className="install-banner">
                    <span className="install-banner-text">Add to your home screen for quick access</span>
                    <button className="install-banner-btn" onClick={handleInstall}>Install</button>
                    <button className="install-banner-dismiss" onClick={() => setInstallPrompt(null)} aria-label="Dismiss">✕</button>
                </div>
            )}
        </main>
    );
};

export default Results;
